/* eslint-disable react/no-unescaped-entities */
/* eslint-disable @typescript-eslint/no-explicit-any */
// Dashboard component for VESTN Admin Panel
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminPage from '../pages/AdminPage';
import BlogPage from '../pages/BlogPage';
import ContactsPage from '../pages/ContactsPage';
import CoursesPage from '../pages/CoursesPage';
import DashboardPage from '../pages/DashboardPage';
import PartnersPage from '../pages/PartnersPage';
import PioneerUsersPage from '../pages/PioneerUsersPage';
import TestnetSignupPage from '../pages/TestnetSignupPage';
import NewsletterPage from '../pages/NewsletterPage';
import PresalePage from '../pages/PresalePage';
import UsersPage from '../pages/UsersPage';
// Hub feature pages (ported from Vestin-AdminFe)
import {
  Store,
  CalendarDays,
  BookText,
  MessagesSquare,
  BookOpen,
  ClipboardList,
  UsersRound,
  LucideIcon,
} from 'lucide-react';
import MarketplaceManagement from '../marketplace/marketplace';
import EventsManagement from '../events/events';
import RegistryManagement from '../registry/registry';
import DiscussionsManagement from '../discussions/discussions';
import InsightsManagement from '../insights/insights';
import WaitlistManagement from '../waitlist/waitlist';
import HubUsersManagement from '../hub-users/hub-users';
import { Toaster } from '../ui/toaster';

// Sidebar buttons for the Hub group. Kept compact (vs. the hand-written buttons
// above) but matches their exact active/hover styling.
const HubNavButton: React.FC<{
  tabKey: string;
  label: string;
  Icon: LucideIcon;
  activeTab: string;
  setActiveTab: (tab: string) => void;
}> = ({ tabKey, label, Icon, activeTab, setActiveTab }) => {
  const isActive = activeTab === tabKey;
  return (
    <button
      onClick={() => setActiveTab(tabKey)}
      className='w-full flex items-center px-4 py-3 text-left rounded-lg transition-all duration-200 hover:scale-[1.02]'
      style={{
        backgroundColor: isActive ? '#095D52' : 'rgba(9, 93, 82, 0.05)',
        color: isActive ? '#ede8c5' : '#b8c5c2',
        fontFamily: 'Work Sans, sans-serif',
        fontWeight: isActive ? '600' : '400',
        boxShadow: isActive
          ? '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)'
          : '0 2px 4px rgba(0, 0, 0, 0.1)',
        border: isActive
          ? '1px solid rgba(187, 228, 37, 0.4)'
          : '1px solid rgba(9, 93, 82, 0.2)',
      }}
      onMouseEnter={(e) => {
        if (!isActive) {
          e.currentTarget.style.backgroundColor = '#095D52';
          e.currentTarget.style.color = '#ede8c5';
          e.currentTarget.style.borderColor = 'rgba(187, 228, 37, 0.3)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(9, 93, 82, 0.3)';
        }
      }}
      onMouseLeave={(e) => {
        if (!isActive) {
          e.currentTarget.style.backgroundColor = 'rgba(9, 93, 82, 0.05)';
          e.currentTarget.style.color = '#b8c5c2';
          e.currentTarget.style.borderColor = 'rgba(9, 93, 82, 0.2)';
          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
        }
      }}
    >
      <Icon className='w-5 h-5 mr-3' />
      {label}
    </button>
  );
};

const HUB_NAV_ITEMS: { tabKey: string; label: string; Icon: LucideIcon }[] = [
  { tabKey: 'hub-users', label: 'Hub Users', Icon: UsersRound },
  { tabKey: 'waitlist', label: 'Waitlist', Icon: ClipboardList },
  { tabKey: 'marketplace', label: 'Marketplace', Icon: Store },
  { tabKey: 'discussions', label: 'Discussions', Icon: MessagesSquare },
  { tabKey: 'events', label: 'Events', Icon: CalendarDays },
  { tabKey: 'insights', label: 'Insights', Icon: BookOpen },
  { tabKey: 'registry', label: 'Registry', Icon: BookText },
];

// Friendly titles for the header bar (activeTab keys -> display label).
const TAB_TITLES: Record<string, string> = {
  'pioneer-users': 'Pioneer Users',
  'testnet-signup': 'Testnet Signup',
  'hub-users': 'Hub Users',
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');

    if (!token || !user) {
      navigate('/login');
      return;
    }

    // Check if user role is admin
    try {
      const userData = JSON.parse(user);
      if (userData.role !== 'admin') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
        return;
      }
    } catch (error) {
      console.error('Failed to parse user data:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/login');
      return;
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <DashboardPage setActiveTab={setActiveTab} />;
      case 'users':
        return <UsersPage />;
      case 'pioneer-users':
        return <PioneerUsersPage />;
      case 'testnet-signup':
        return <TestnetSignupPage />;
      case 'newsletter':
        return <NewsletterPage />;
      case 'admin':
        return <AdminPage />;
      case 'courses':
        return <CoursesPage />;
      case 'presale':
        return <PresalePage />;
      case 'contacts':
        return <ContactsPage />;
      case 'partners':
        return <PartnersPage />;
      case 'blog':
        return <BlogPage />;
      case 'marketplace':
        return <MarketplaceManagement />;
      case 'events':
        return <EventsManagement />;
      case 'registry':
        return <RegistryManagement />;
      case 'discussions':
        return <DiscussionsManagement />;
      case 'insights':
        return <InsightsManagement />;
      case 'waitlist':
        return <WaitlistManagement />;
      case 'hub-users':
        return <HubUsersManagement />;
      default:
        return <DashboardPage setActiveTab={setActiveTab} />;
    }
  };

  return (
    <div className='min-h-screen flex' style={{ backgroundColor: '#0B222C' }}>
      {/* Sidebar */}
      <div
        className='w-64 flex flex-col shadow-2xl'
        style={{
          backgroundColor: '#0B222C',
          borderRight: '2px solid #095D52',
          boxShadow:
            '4px 0 25px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.15), inset -1px 0 0 rgba(187, 228, 37, 0.1)',
          backgroundImage:
            'linear-gradient(180deg, rgba(187, 228, 37, 0.08) 0%, rgba(9, 93, 82, 0.12) 50%, rgba(11, 34, 44, 0.95) 100%)',
        }}
      >
        <div className='px-6 py-8' style={{}}>
          <div className='flex items-center space-x-3'>
            <img
              src='/VESTN_FullLogo.png'
              alt='VESTN Admin'
              className='h-24 w-auto'
              style={{
                filter:
                  'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.5)) drop-shadow(0 0 15px rgba(187, 228, 37, 0.3))',
              }}
            />
          </div>
        </div>

        <nav className='flex-1 px-6 py-4 space-y-2'>
          {/* Hub group */}
          <div
            className='pt-1 pb-1 px-2 text-xs font-semibold uppercase tracking-wider'
            style={{ color: '#8aa39d', fontFamily: 'Work Sans, sans-serif', letterSpacing: '1px' }}
          >
            Hub
          </div>
          {HUB_NAV_ITEMS.map((item) => (
            <HubNavButton
              key={item.tabKey}
              tabKey={item.tabKey}
              label={item.label}
              Icon={item.Icon}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
          ))}

          {/* Ecosystem group */}
          <div
            className='pt-4 pb-1 px-2 text-xs font-semibold uppercase tracking-wider'
            style={{ color: '#8aa39d', fontFamily: 'Work Sans, sans-serif', letterSpacing: '1px' }}
          >
            Ecosystem
          </div>
          <button
            onClick={() => setActiveTab('dashboard')}
            className='w-full flex items-center px-4 py-3 text-left rounded-lg transition-all duration-200 hover:scale-[1.02]'
            style={{
              backgroundColor: activeTab === 'dashboard' ? '#095D52' : 'rgba(9, 93, 82, 0.05)',
              color: activeTab === 'dashboard' ? '#ede8c5' : '#b8c5c2',
              fontFamily: 'Work Sans, sans-serif',
              fontWeight: activeTab === 'dashboard' ? '600' : '400',
              boxShadow:
                activeTab === 'dashboard'
                  ? '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)'
                  : '0 2px 4px rgba(0, 0, 0, 0.1)',
              border:
                activeTab === 'dashboard'
                  ? '1px solid rgba(187, 228, 37, 0.4)'
                  : '1px solid rgba(9, 93, 82, 0.2)',
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'dashboard') {
                e.currentTarget.style.backgroundColor = '#095D52';
                e.currentTarget.style.color = '#ede8c5';
                e.currentTarget.style.borderColor = 'rgba(187, 228, 37, 0.3)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(9, 93, 82, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'dashboard') {
                e.currentTarget.style.backgroundColor = 'rgba(9, 93, 82, 0.05)';
                e.currentTarget.style.color = '#b8c5c2';
                e.currentTarget.style.borderColor = 'rgba(9, 93, 82, 0.2)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
              }
            }}
          >
            <svg className='w-5 h-5 mr-3' fill='currentColor' viewBox='0 0 20 20'>
              <path d='M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z' />
            </svg>
            Dashboard
          </button>

          <button
            onClick={() => setActiveTab('users')}
            className='w-full flex items-center px-4 py-3 text-left rounded-lg transition-all duration-200 hover:scale-[1.02]'
            style={{
              backgroundColor: activeTab === 'users' ? '#095D52' : 'rgba(9, 93, 82, 0.05)',
              color: activeTab === 'users' ? '#ede8c5' : '#b8c5c2',
              fontFamily: 'Work Sans, sans-serif',
              fontWeight: activeTab === 'users' ? '600' : '400',
              boxShadow:
                activeTab === 'users'
                  ? '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)'
                  : '0 2px 4px rgba(0, 0, 0, 0.1)',
              border:
                activeTab === 'users'
                  ? '1px solid rgba(187, 228, 37, 0.4)'
                  : '1px solid rgba(9, 93, 82, 0.2)',
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'users') {
                e.currentTarget.style.backgroundColor = '#095D52';
                e.currentTarget.style.color = '#ede8c5';
                e.currentTarget.style.borderColor = 'rgba(187, 228, 37, 0.3)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(9, 93, 82, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'users') {
                e.currentTarget.style.backgroundColor = 'rgba(9, 93, 82, 0.05)';
                e.currentTarget.style.color = '#b8c5c2';
                e.currentTarget.style.borderColor = 'rgba(9, 93, 82, 0.2)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
              }
            }}
          >
            <svg className='w-5 h-5 mr-3' fill='currentColor' viewBox='0 0 20 20'>
              <path d='M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z' />
            </svg>
            Users
          </button>

          <button
            onClick={() => setActiveTab('pioneer-users')}
            className='w-full flex items-center px-4 py-3 text-left rounded-lg transition-all duration-200 hover:scale-[1.02]'
            style={{
              backgroundColor: activeTab === 'pioneer-users' ? '#095D52' : 'rgba(9, 93, 82, 0.05)',
              color: activeTab === 'pioneer-users' ? '#ede8c5' : '#b8c5c2',
              fontFamily: 'Work Sans, sans-serif',
              fontWeight: activeTab === 'pioneer-users' ? '600' : '400',
              boxShadow:
                activeTab === 'pioneer-users'
                  ? '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)'
                  : '0 2px 4px rgba(0, 0, 0, 0.1)',
              border:
                activeTab === 'pioneer-users'
                  ? '1px solid rgba(187, 228, 37, 0.4)'
                  : '1px solid rgba(9, 93, 82, 0.2)',
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'pioneer-users') {
                e.currentTarget.style.backgroundColor = '#095D52';
                e.currentTarget.style.color = '#ede8c5';
                e.currentTarget.style.borderColor = 'rgba(187, 228, 37, 0.3)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(9, 93, 82, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'pioneer-users') {
                e.currentTarget.style.backgroundColor = 'rgba(9, 93, 82, 0.05)';
                e.currentTarget.style.color = '#b8c5c2';
                e.currentTarget.style.borderColor = 'rgba(9, 93, 82, 0.2)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
              }
            }}
          >
            <svg className='w-5 h-5 mr-3' fill='currentColor' viewBox='0 0 20 20'>
              <path d='M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z' />
            </svg>
            Pioneer Users
          </button>

          <button
            onClick={() => setActiveTab('testnet-signup')}
            className='w-full flex items-center px-4 py-3 text-left rounded-lg transition-all duration-200 hover:scale-[1.02]'
            style={{
              backgroundColor: activeTab === 'testnet-signup' ? '#095D52' : 'rgba(9, 93, 82, 0.05)',
              color: activeTab === 'testnet-signup' ? '#ede8c5' : '#b8c5c2',
              fontFamily: 'Work Sans, sans-serif',
              fontWeight: activeTab === 'testnet-signup' ? '600' : '400',
              boxShadow:
                activeTab === 'testnet-signup'
                  ? '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)'
                  : '0 2px 4px rgba(0, 0, 0, 0.1)',
              border:
                activeTab === 'testnet-signup'
                  ? '1px solid rgba(187, 228, 37, 0.4)'
                  : '1px solid rgba(9, 93, 82, 0.2)',
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'testnet-signup') {
                e.currentTarget.style.backgroundColor = '#095D52';
                e.currentTarget.style.color = '#ede8c5';
                e.currentTarget.style.borderColor = 'rgba(187, 228, 37, 0.3)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(9, 93, 82, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'testnet-signup') {
                e.currentTarget.style.backgroundColor = 'rgba(9, 93, 82, 0.05)';
                e.currentTarget.style.color = '#b8c5c2';
                e.currentTarget.style.borderColor = 'rgba(9, 93, 82, 0.2)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
              }
            }}
          >
            <svg className='w-5 h-5 mr-3' fill='currentColor' viewBox='0 0 20 20'>
              <path d='M2 6a2 2 0 012-2h12a2 2 0 012 2v1H2V6zm0 3h16v5a2 2 0 01-2 2H4a2 2 0 01-2-2V9z' />
            </svg>
            Testnet Signup
          </button>

          <button
            onClick={() => setActiveTab('newsletter')}
            className='w-full flex items-center px-4 py-3 text-left rounded-lg transition-all duration-200 hover:scale-[1.02]'
            style={{
              backgroundColor: activeTab === 'newsletter' ? '#095D52' : 'rgba(9, 93, 82, 0.05)',
              color: activeTab === 'newsletter' ? '#ede8c5' : '#b8c5c2',
              fontFamily: 'Work Sans, sans-serif',
              fontWeight: activeTab === 'newsletter' ? '600' : '400',
              boxShadow:
                activeTab === 'newsletter'
                  ? '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)'
                  : '0 2px 4px rgba(0, 0, 0, 0.1)',
              border:
                activeTab === 'newsletter'
                  ? '1px solid rgba(187, 228, 37, 0.4)'
                  : '1px solid rgba(9, 93, 82, 0.2)',
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'newsletter') {
                e.currentTarget.style.backgroundColor = '#095D52';
                e.currentTarget.style.color = '#ede8c5';
                e.currentTarget.style.borderColor = 'rgba(187, 228, 37, 0.3)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(9, 93, 82, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'newsletter') {
                e.currentTarget.style.backgroundColor = 'rgba(9, 93, 82, 0.05)';
                e.currentTarget.style.color = '#b8c5c2';
                e.currentTarget.style.borderColor = 'rgba(9, 93, 82, 0.2)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
              }
            }}
          >
            <svg className='w-5 h-5 mr-3' fill='currentColor' viewBox='0 0 20 20'>
              <path d='M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z' />
              <path d='M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z' />
            </svg>
            Newsletter
          </button>

          <button
            onClick={() => setActiveTab('admin')}
            className='w-full flex items-center px-4 py-3 text-left rounded-lg transition-all duration-200 hover:scale-[1.02]'
            style={{
              backgroundColor: activeTab === 'admin' ? '#095D52' : 'rgba(9, 93, 82, 0.05)',
              color: activeTab === 'admin' ? '#ede8c5' : '#b8c5c2',
              fontFamily: 'Work Sans, sans-serif',
              fontWeight: activeTab === 'admin' ? '600' : '400',
              boxShadow:
                activeTab === 'admin'
                  ? '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)'
                  : '0 2px 4px rgba(0, 0, 0, 0.1)',
              border:
                activeTab === 'admin'
                  ? '1px solid rgba(187, 228, 37, 0.4)'
                  : '1px solid rgba(9, 93, 82, 0.2)',
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'admin') {
                e.currentTarget.style.backgroundColor = '#095D52';
                e.currentTarget.style.color = '#ede8c5';
                e.currentTarget.style.borderColor = 'rgba(187, 228, 37, 0.3)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(9, 93, 82, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'admin') {
                e.currentTarget.style.backgroundColor = 'rgba(9, 93, 82, 0.05)';
                e.currentTarget.style.color = '#b8c5c2';
                e.currentTarget.style.borderColor = 'rgba(9, 93, 82, 0.2)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
              }
            }}
          >
            <svg className='w-5 h-5 mr-3' fill='currentColor' viewBox='0 0 20 20'>
              <path
                fillRule='evenodd'
                d='M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z'
                clipRule='evenodd'
              />
            </svg>
            Admin
          </button>

          <button
            onClick={() => setActiveTab('courses')}
            className='w-full flex items-center px-4 py-3 text-left rounded-lg transition-all duration-200 hover:scale-[1.02]'
            style={{
              backgroundColor: activeTab === 'courses' ? '#095D52' : 'rgba(9, 93, 82, 0.05)',
              color: activeTab === 'courses' ? '#ede8c5' : '#b8c5c2',
              fontFamily: 'Work Sans, sans-serif',
              fontWeight: activeTab === 'courses' ? '600' : '400',
              boxShadow:
                activeTab === 'courses'
                  ? '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)'
                  : '0 2px 4px rgba(0, 0, 0, 0.1)',
              border:
                activeTab === 'courses'
                  ? '1px solid rgba(187, 228, 37, 0.4)'
                  : '1px solid rgba(9, 93, 82, 0.2)',
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'courses') {
                e.currentTarget.style.backgroundColor = '#095D52';
                e.currentTarget.style.color = '#ede8c5';
                e.currentTarget.style.borderColor = 'rgba(187, 228, 37, 0.3)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(9, 93, 82, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'courses') {
                e.currentTarget.style.backgroundColor = 'rgba(9, 93, 82, 0.05)';
                e.currentTarget.style.color = '#b8c5c2';
                e.currentTarget.style.borderColor = 'rgba(9, 93, 82, 0.2)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
              }
            }}
          >
            <svg className='w-5 h-5 mr-3' fill='currentColor' viewBox='0 0 20 20'>
              <path
                fillRule='evenodd'
                d='M10 2L3 7v11a1 1 0 001 1h12a1 1 0 001-1V7l-7-5zM8 15v-4h4v4H8zm6-6H6V8h8v1z'
                clipRule='evenodd'
              />
            </svg>
            Courses
          </button>

          <button
            onClick={() => setActiveTab('presale')}
            className='w-full flex items-center px-4 py-3 text-left rounded-lg transition-all duration-200 hover:scale-[1.02]'
            style={{
              backgroundColor: activeTab === 'presale' ? '#095D52' : 'rgba(9, 93, 82, 0.05)',
              color: activeTab === 'presale' ? '#ede8c5' : '#b8c5c2',
              fontFamily: 'Work Sans, sans-serif',
              fontWeight: activeTab === 'presale' ? '600' : '400',
              boxShadow:
                activeTab === 'presale'
                  ? '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)'
                  : '0 2px 4px rgba(0, 0, 0, 0.1)',
              border:
                activeTab === 'presale'
                  ? '1px solid rgba(187, 228, 37, 0.4)'
                  : '1px solid rgba(9, 93, 82, 0.2)',
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'presale') {
                e.currentTarget.style.backgroundColor = '#095D52';
                e.currentTarget.style.color = '#ede8c5';
                e.currentTarget.style.borderColor = 'rgba(187, 228, 37, 0.3)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(9, 93, 82, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'presale') {
                e.currentTarget.style.backgroundColor = 'rgba(9, 93, 82, 0.05)';
                e.currentTarget.style.color = '#b8c5c2';
                e.currentTarget.style.borderColor = 'rgba(9, 93, 82, 0.2)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
              }
            }}
          >
            <svg className='w-5 h-5 mr-3' fill='currentColor' viewBox='0 0 20 20'>
              <path
                fillRule='evenodd'
                d='M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z'
                clipRule='evenodd'
              />
            </svg>
            Presale
          </button>

          <button
            onClick={() => setActiveTab('contacts')}
            className='w-full flex items-center px-4 py-3 text-left rounded-lg transition-all duration-200 hover:scale-[1.02]'
            style={{
              backgroundColor: activeTab === 'contacts' ? '#095D52' : 'rgba(9, 93, 82, 0.05)',
              color: activeTab === 'contacts' ? '#ede8c5' : '#b8c5c2',
              fontFamily: 'Work Sans, sans-serif',
              fontWeight: activeTab === 'contacts' ? '600' : '400',
              boxShadow:
                activeTab === 'contacts'
                  ? '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)'
                  : '0 2px 4px rgba(0, 0, 0, 0.1)',
              border:
                activeTab === 'contacts'
                  ? '1px solid rgba(187, 228, 37, 0.4)'
                  : '1px solid rgba(9, 93, 82, 0.2)',
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'contacts') {
                e.currentTarget.style.backgroundColor = '#095D52';
                e.currentTarget.style.color = '#ede8c5';
                e.currentTarget.style.borderColor = 'rgba(187, 228, 37, 0.3)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(9, 93, 82, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'contacts') {
                e.currentTarget.style.backgroundColor = 'rgba(9, 93, 82, 0.05)';
                e.currentTarget.style.color = '#b8c5c2';
                e.currentTarget.style.borderColor = 'rgba(9, 93, 82, 0.2)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
              }
            }}
          >
            <svg className='w-5 h-5 mr-3' fill='currentColor' viewBox='0 0 20 20'>
              <path
                fillRule='evenodd'
                d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z'
                clipRule='evenodd'
              />
            </svg>
            Contacts
          </button>

          <button
            onClick={() => setActiveTab('partners')}
            className='w-full flex items-center px-4 py-3 text-left rounded-lg transition-all duration-200 hover:scale-[1.02]'
            style={{
              backgroundColor: activeTab === 'partners' ? '#095D52' : 'rgba(9, 93, 82, 0.05)',
              color: activeTab === 'partners' ? '#ede8c5' : '#b8c5c2',
              fontFamily: 'Work Sans, sans-serif',
              fontWeight: activeTab === 'partners' ? '600' : '400',
              boxShadow:
                activeTab === 'partners'
                  ? '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)'
                  : '0 2px 4px rgba(0, 0, 0, 0.1)',
              border:
                activeTab === 'partners'
                  ? '1px solid rgba(187, 228, 37, 0.4)'
                  : '1px solid rgba(9, 93, 82, 0.2)',
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'partners') {
                e.currentTarget.style.backgroundColor = '#095D52';
                e.currentTarget.style.color = '#ede8c5';
                e.currentTarget.style.borderColor = 'rgba(187, 228, 37, 0.3)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(9, 93, 82, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'partners') {
                e.currentTarget.style.backgroundColor = 'rgba(9, 93, 82, 0.05)';
                e.currentTarget.style.color = '#b8c5c2';
                e.currentTarget.style.borderColor = 'rgba(9, 93, 82, 0.2)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
              }
            }}
          >
            <svg className='w-5 h-5 mr-3' fill='currentColor' viewBox='0 0 20 20'>
              <path
                fillRule='evenodd'
                d='M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z'
                clipRule='evenodd'
              />
            </svg>
            Partners
          </button>

          <button
            onClick={() => setActiveTab('blog')}
            className='w-full flex items-center px-4 py-3 text-left rounded-lg transition-all duration-200 hover:scale-[1.02]'
            style={{
              backgroundColor: activeTab === 'blog' ? '#095D52' : 'rgba(9, 93, 82, 0.05)',
              color: activeTab === 'blog' ? '#ede8c5' : '#b8c5c2',
              fontFamily: 'Work Sans, sans-serif',
              fontWeight: activeTab === 'blog' ? '600' : '400',
              boxShadow:
                activeTab === 'blog'
                  ? '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)'
                  : '0 2px 4px rgba(0, 0, 0, 0.1)',
              border:
                activeTab === 'blog'
                  ? '1px solid rgba(187, 228, 37, 0.4)'
                  : '1px solid rgba(9, 93, 82, 0.2)',
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'blog') {
                e.currentTarget.style.backgroundColor = '#095D52';
                e.currentTarget.style.color = '#ede8c5';
                e.currentTarget.style.borderColor = 'rgba(187, 228, 37, 0.3)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(9, 93, 82, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'blog') {
                e.currentTarget.style.backgroundColor = 'rgba(9, 93, 82, 0.05)';
                e.currentTarget.style.color = '#b8c5c2';
                e.currentTarget.style.borderColor = 'rgba(9, 93, 82, 0.2)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
              }
            }}
          >
            <svg className='w-5 h-5 mr-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
              />
            </svg>
            Blog
          </button>
        </nav>

        <div
          className='px-6 py-6'
          style={{
            borderTop: '2px solid #095D52',
            backgroundImage:
              'linear-gradient(135deg, rgba(187, 228, 37, 0.08) 0%, rgba(9, 93, 82, 0.12) 50%, rgba(11, 34, 44, 0.9) 100%)',
            boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.3)',
          }}
        >
          <button
            onClick={handleLogout}
            className='w-full flex items-center px-4 py-3 text-left rounded-lg transition-all duration-200 hover:scale-[1.02]'
            style={{
              color: '#b8c5c2',
              fontFamily: 'Work Sans, sans-serif',
              fontWeight: '500',
              backgroundColor: 'rgba(9, 93, 82, 0.05)',
              border: '1px solid rgba(9, 93, 82, 0.2)',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#095D52';
              e.currentTarget.style.color = '#ede8c5';
              e.currentTarget.style.borderColor = 'rgba(187, 228, 37, 0.4)';
              e.currentTarget.style.boxShadow =
                '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(9, 93, 82, 0.05)';
              e.currentTarget.style.color = '#b8c5c2';
              e.currentTarget.style.borderColor = 'rgba(9, 93, 82, 0.2)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
            }}
          >
            <svg className='w-5 h-5 mr-3' fill='currentColor' viewBox='0 0 20 20'>
              <path
                fillRule='evenodd'
                d='M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z'
                clipRule='evenodd'
              />
            </svg>
            Logout
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className='flex-1 flex flex-col'>
        {/* Header */}
        <header
          className='p-6 shadow-lg'
          style={{
            backgroundColor: '#0B222C',
            borderBottom: '2px solid #095D52',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.1)',
            backgroundImage:
              'linear-gradient(135deg, rgba(187, 228, 37, 0.05) 0%, rgba(9, 93, 82, 0.08) 100%)',
          }}
        >
          <div className='flex justify-between items-center'>
            <h2
              className='text-2xl font-bold capitalize font-dm-serif'
              style={{
                color: '#ede8c5',
                textShadow: '0 2px 6px rgba(0, 0, 0, 0.4), 0 0 10px rgba(187, 228, 37, 0.2)',
                letterSpacing: '0.5px',
              }}
            >
              {TAB_TITLES[activeTab] ?? activeTab} Management
            </h2>
            <div className='flex items-center space-x-4'>
              <div
                className='flex items-center space-x-3 px-4 py-2 rounded-lg'
                style={{
                  backgroundColor: 'rgba(9, 93, 82, 0.1)',
                  border: '1px solid rgba(187, 228, 37, 0.2)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                }}
              >
                <div
                  className='w-2 h-2 rounded-full'
                  style={{
                    backgroundColor: '#4ade80',
                    boxShadow: '0 0 8px rgba(74, 222, 128, 0.6)',
                  }}
                />
                <span
                  className='font-work-sans font-medium'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.3px',
                  }}
                >
                  Welcome, Admin
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <main
          className='flex-1 px-8 py-8 overflow-y-auto'
          style={{
            backgroundColor: '#0B222C',
            backgroundImage:
              'radial-gradient(circle at 20% 80%, rgba(187, 228, 37, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(9, 93, 82, 0.08) 0%, transparent 50%)',
          }}
        >
          {renderContent()}
        </main>
      </div>
      <Toaster />
    </div>
  );
};

export default Dashboard;
